import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { DateTime, Duration, FixedOffsetZone, SystemZone, type Zone } from 'luxon';

import { CostRuleWireFormat } from '@core/types/api/CostRule/CostRule';
import { DeliveryCostWireFormat } from '@core/types/api/DeliveryCost/DeliveryCost';

import type { AddressWireFormat } from '../types/api/Address/Address';
import type { CostWireFormat } from '../types/api/Cost/Cost';
import { Currency, CurrencyWireFormat } from '../types/api/Currency/Currency';
import Enums from '../types/api/Enums';
import { hasValue } from './equality';
import { logger } from './logger';
import { NIL_UUID, isValidUUID } from './uuid';

export type LookupInputOption = {
  /** The value that formik will use when setting values */
  value: any;
  /** The display label in the lookup */
  label: string;
  /** Optional sublabels */
  sublabels?: string[];
  /** Optional grouping */
  group?: string;
  /** @default false */
  isDisabled?: boolean;
};

/**
 * Converts a possibly-null or empty string to null, preserving non-empty strings.
 */
export const ParseNullableString = <T extends string>(input: T | null | undefined): T | null => {
  if (!hasValue(input) || input === '') {
    return null;
  }
  return input;
};

/**
 * Parses a numeric string, stripping commas and whitespace.
 * Returns null if the input is not a finite number.
 */
export const ParseNullableNumericString = (input: string | null): string | null => {
  if (!hasValue(input)) {
    return null;
  }
  const value = input.trim().replaceAll(',', '');
  if (value === '' || !Number.isFinite(Number(value))) {
    return null;
  }
  return value;
};

/**
 * Parses a numeric string, defaulting to empty string if invalid.
 */
export const ParseNumericString = (input: string): string =>
  ParseNullableNumericString(input) ?? '';

/**
 * Parses a numeric string to an actual number, returning null if invalid.
 */
export const ParseNullableNumber = (input: string): number | null => {
  const parsedString = ParseNullableNumericString(input);
  const number = Number(parsedString);
  if (parsedString === null || !Number.isFinite(number)) {
    return null;
  }
  return number;
};

export const ParseNullablePercentageAsRatio = (input: string): number | null => {
  const percentage = ParseNullableNumber(input);
  if (percentage === null) {
    return null;
  }
  return percentage / 100;
};

export const ParsePercentageAsRatio = (input: string): string =>
  ParseNullablePercentageAsRatio(input)?.toFixed(3) ?? '';

/**
 * Parses a Currency object, stripping commas from the number field.
 * Returns null if the currency is invalid (null number or currency code).
 */
export const ParseNullableCurrency = (
  input: CurrencyWireFormat | null,
): CurrencyWireFormat | null => {
  if (input === null) {
    return null;
  }
  const number = ParseNullableNumericString(input.number);
  const currency = ParseNullableString(input.currency);
  if (number === null || currency === null) {
    return null;
  }
  return { number, currency };
};

/**
 * Parses a Currency object, defaulting to zero if invalid.
 */
export const ParseCurrency = (input: CurrencyWireFormat): CurrencyWireFormat =>
  ParseNullableCurrency(input) ?? Currency.zero();

export const ParseNullableCost = (input: CostWireFormat | null): CostWireFormat | null => {
  // Inner transforms run after outer transforms so cast it beforehand
  const amount = ParseNullableCurrency(input?.amount ?? null);
  const unit = input?.unit ?? null;
  if (amount === null || unit === null || unit === '') {
    return null;
  }
  return { amount, unit };
};

export const ParseNullablePhoneNumber = (input: string): string | null => {
  if (typeof input !== 'string') {
    logger.error('attempted to parse a non-string to a phone number:', JSON.stringify(input));
    return null;
  }
  try {
    return parsePhoneNumberWithError(input, 'US').formatInternational();
  } catch {
    if (input === '') {
      return null;
    }
    return input;
  }
};

export const ParsePhoneNumber = (input: string): string => ParseNullablePhoneNumber(input) ?? '';

export const ParseNullableLocalDate = (
  input: string,
  zone: Zone = SystemZone.instance,
): string | null => {
  const string = ParseNullableString(input);
  if (string === null) {
    return null;
  }
  const date = DateTime.fromISO(string, { zone });
  if (!date.isValid) {
    return null;
  }
  return date.toISO();
};

export const ParseLocalDate = (input: string, zone: Zone = SystemZone.instance): string =>
  ParseNullableLocalDate(input, zone) ?? '';

export const ParseNullableUTCDate = (input: string): string | null => {
  const string = ParseNullableString(input);
  if (string === null) {
    return null;
  }
  const date = DateTime.fromISO(string, { zone: FixedOffsetZone.utcInstance }).toUTC();
  if (!date.isValid) {
    return null;
  }
  return date.toISO();
};

export const ParseUTCDate = (input: string): string => ParseNullableUTCDate(input) ?? '';

export const ParseNullableDuration = (input: string): string | null => {
  const string = ParseNullableString(input);
  if (string === null) {
    return null;
  }
  const duration = Duration.fromISO(string);
  if (!duration.isValid) {
    return null;
  }
  return duration.toISO();
};

export const ParseDuration = (input: string): string => ParseNullableDuration(input) ?? '';

type Reference = {
  id: string | null;
  option: LookupInputOption | null;
};

export const ParseNullableReference = (input: Reference | null): { id: string } | null => {
  if (input === null || input.id === null || !isValidUUID(input.id)) {
    return null;
  }
  return { id: input.id };
};

export const ParseReference = (input: Reference): { id: string } =>
  ParseNullableReference(input) ?? { id: NIL_UUID };

export const ParseReferencesArray = (
  input?: (Reference | undefined | null)[] | null,
): { id: string }[] => {
  if (!hasValue(input)) {
    return [];
  }
  const refs = input.filter((item) => isValidUUID(item?.id)) as { id: string }[];
  // Strip non-id properties from the return value (e.g. `option`).
  return refs.map(({ id }) => ({ id }));
};

export const ParseAddress = (input: Partial<AddressWireFormat>): AddressWireFormat => ({
  line1: ParseNullableString(input.line1),
  line2: ParseNullableString(input.line2),
  city: ParseNullableString(input.city),
  state: input.state ?? '',
  postalCode: ParseNullableString(input.postalCode),
  country: ParseNullableString(input.country),
  latitude: ParseNullableString(input.latitude),
  longitude: ParseNullableString(input.longitude),
});

export const ParseDeliveryCost = (
  input: Omit<DeliveryCostWireFormat, 'totalDeliveryCost'>,
): Omit<DeliveryCostWireFormat, 'totalDeliveryCost'> | null => ({
  deliveryCostOverride: ParseNullableCost(input.deliveryCostOverride),
  perMinCost: ParseCurrency(input.perMinCost),
  driveToJobTime: ParseDuration(input.driveToJobTime),
  driveToPlantTime: ParseDuration(input.driveToPlantTime),
  loadTime: ParseDuration(input.loadTime),
  loadedInYardTime: ParseDuration(input.loadedInYardTime),
  pourTime: ParseDuration(input.pourTime),
  unloadedAtJobTime: ParseDuration(input.unloadedAtJobTime),
  waitTime: ParseDuration(input.waitTime),
  drivingTimeAdjustmentPercentage: ParsePercentageAsRatio(input.drivingTimeAdjustmentPercentage),
  unengagedTimeAdjustmentPercentage: ParsePercentageAsRatio(
    input.unengagedTimeAdjustmentPercentage,
  ),
  loadSizeUnit: input.loadSizeUnit,
  loadSize: ParseNumericString(input.loadSize),
  fuelVolumeUnit: input.fuelVolumeUnit,
  fuelCostPerUnit: ParseCurrency(input.fuelCostPerUnit),
  driveDistanceUnit: input.driveDistanceUnit,
  driveDistanceToJob: ParseNumericString(input.driveDistanceToJob),
  driveDistanceToPlant: ParseNumericString(input.driveDistanceToPlant),
  driveDistancePerFuelVolume: ParseNumericString(input.driveDistancePerFuelVolume),
});

export const ParseNullableDeliveryCost = (
  input: Omit<DeliveryCostWireFormat, 'totalDeliveryCost'> | null,
): Omit<DeliveryCostWireFormat, 'totalDeliveryCost'> | null => {
  if (input === null) {
    return null;
  }
  return ParseDeliveryCost(input);
};

/** Conditionally transform a value to be null if it is not the selected 'kind'. */
export const ParseCostRule = (input: CostRuleWireFormat): CostRuleWireFormat => {
  switch (input.kind) {
    case Enums.CostRuleKind.Flat:
      return {
        ...input,
        markupRatio: null,
      };
    case Enums.CostRuleKind.Ratio:
      return {
        ...input,
        flatChange: null,
      };
    default:
      return input;
  }
};

export const ParseNullableCostRule = (
  input: CostRuleWireFormat | null,
): CostRuleWireFormat | null => {
  if (input === null) {
    return null;
  }
  return ParseCostRule(input);
};
